import { useState, useEffect } from "react";
import { Clapperboard, Music4, Smartphone, ChevronLeft, Mic2, RotateCcw } from "lucide-react";
import DirectorProjectStudio from "@/components/mist/DirectorProjectStudio";
import MusicVisualizerStudio from "@/components/mist/MusicVisualizerStudio";
import SingingCharacterStudio from "@/components/mist/SingingCharacterStudio";
import ReelTimelineEditor from "@/components/mist/ReelTimelineEditor";
import { directorApi } from "@/api/mist";
import "@/styles/workspace.css";

/* ==========================================================================
   DIRECTOR STUDIO — unified macro-tab shell.
   Tab 1 Director Mode (sequential MP4 timeline) · Tab 2 Music Viz (preserved)
   · Tab 3 Reels (fast social lane — Phase-later placeholder).
   Lifecycle rule: exactly ONE heavy tab tree is mounted at a time
   (conditional render, not CSS hide) so we never run two video decoders /
   blob-URL pools at once on the 1 vCPU pod.
   ========================================================================== */
const GOLD = "#FFE08A";

const TABS = [
  { k: "director", label: "Director Mode", Icon: Clapperboard },
  { k: "viz", label: "Music Viz", Icon: Music4 },
  { k: "singer", label: "Singer", Icon: Mic2 },
  { k: "reels", label: "Reels", Icon: Smartphone },
];

export default function DirectorStudio({ onExit, initialTab }) {
  const [tab, setTab] = useState(TABS.some((t) => t.k === initialTab) ? initialTab : "director");
  const [title, setTitle] = useState("Untitled Project");
  // Bump to force-remount the active tab tree → every child re-inits to a clean
  // slate (draft keys are cleared just before), giving a reliable full reset.
  const [resetNonce, setResetNonce] = useState(0);

  const resetActive = () => {
    if (!window.confirm("Restart this project? Everything — name, scenes and uploaded pictures — will be wiped.")) return;
    try {
      localStorage.removeItem("mist_active_project_vault"); // Director Mode crash-recovery draft
      localStorage.removeItem("mist_ws1_draft");            // Singer Workspace-1 draft
    } catch { /* ignore */ }
    directorApi.clearCast().catch(() => {});                // wipe uploaded character-lock cast
    setTitle("Untitled Project");
    setResetNonce((n) => n + 1);
  };

  // Programmatic tab switches (e.g. Singer → Music Viz handoff) push ?dstab=…
  // then fire popstate; re-read it here so the macro-tab follows the URL.
  useEffect(() => {
    const onPop = () => {
      const t = new URLSearchParams(window.location.search).get("dstab");
      if (t && TABS.some((x) => x.k === t)) setTab(t);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  return (
    <div className="h-full w-full flex flex-col bg-[#0A0A0A]" data-testid="director-studio">
      {/* macro header: exit · project title · tab pills */}
      <div className="shrink-0 border-b-2 border-[#1a1a1a] bg-[#0e0e0e] director-studio-main-header">
        <div className="flex items-center gap-2 px-3 py-2.5">
          {onExit && (
            <button onClick={onExit} data-testid="director-studio-exit"
              className="w-9 h-9 grid place-items-center border-2 border-black bg-[#141414] shadow-[2px_2px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
              <ChevronLeft size={16} className="text-white" />
            </button>
          )}
          <input value={title} onChange={(e) => setTitle(e.target.value.slice(0, 80))}
            data-testid="director-studio-title"
            className="flex-1 min-w-0 bg-transparent border-b-2 border-transparent focus:border-[#FFE08A] text-white font-serif text-lg leading-none px-1 py-1 outline-none"
            placeholder="Untitled Project" />
          <button onClick={resetActive} data-testid="director-studio-reset"
            title="Restart — wipe the whole project (name, scenes, uploads) and start fresh"
            className="shrink-0 flex items-center gap-1 h-9 px-2.5 border-2 border-black bg-[#141414] text-white/70 font-mono text-[9px] font-black uppercase tracking-widest shadow-[2px_2px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
            <RotateCcw size={13} /> Restart
          </button>
        </div>
        <div className="flex gap-1 px-2 pb-2 studio-tabs-navigation-row" data-testid="director-studio-tabs">
          {TABS.map(({ k, label, Icon }) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`studio-tab-${k}`}
              className={`flex-1 flex items-center justify-center gap-1.5 border-2 border-black px-2 py-2 font-mono text-[9px] font-black uppercase tracking-widest transition-[transform,box-shadow] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none ${
                tab === k ? "bg-[#FFE08A] text-black shadow-[2px_2px_0px_#000]" : "bg-[#121212] text-white/55"
              }`}>
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>
      </div>

      {/* body — one heavy tree mounted at a time */}
      <div className="flex-1 min-h-0">
        {tab === "director" && <DirectorProjectStudio key={`director-${resetNonce}`} />}
        {tab === "viz" && <MusicVisualizerStudio key={`viz-${resetNonce}`} onExit={() => setTab("director")} />}
        {tab === "singer" && <SingingCharacterStudio key={`singer-${resetNonce}`} />}
        {tab === "reels" && (
          <ReelTimelineEditor key={`reels-${resetNonce}`}
            onPublished={() => { sessionStorage.setItem("mist_shell_view", "feed"); window.location.href = window.location.pathname; }} />
        )}
      </div>
    </div>
  );
}
